import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import TinyColor from 'tinycolor2';

import MetaTags from 'components/meta-tags';
import { DUTCHIE_PRIVACY_URL, DUTCHIE_TERMS_URL } from 'shared/constants/legal-urls';
import { Button } from 'components/core';
import { RibbonStarV2 } from 'src/assets/ribbon-star-v2';
import getLocationsFromCoordinates from 'src/utils/get-locations-from-coordinates';
import { getWebCustomizationSettings } from 'src/theme/theme.utils';
import type { LandingPageProps } from '../types';

const US_STATES: Record<string, string> = {
  AL: 'Alabama',
  AK: 'Alaska',
  AZ: 'Arizona',
  AR: 'Arkansas',
  CA: 'California',
  CO: 'Colorado',
  CT: 'Connecticut',
  DE: 'Delaware',
  FL: 'Florida',
  GA: 'Georgia',
  HI: 'Hawaii',
  ID: 'Idaho',
  IL: 'Illinois',
  IN: 'Indiana',
  IA: 'Iowa',
  KS: 'Kansas',
  KY: 'Kentucky',
  LA: 'Louisiana',
  ME: 'Maine',
  MD: 'Maryland',
  MA: 'Massachusetts',
  MI: 'Michigan',
  MN: 'Minnesota',
  MS: 'Mississippi',
  MO: 'Missouri',
  MT: 'Montana',
  NE: 'Nebraska',
  NV: 'Nevada',
  NH: 'New Hampshire',
  NJ: 'New Jersey',
  NM: 'New Mexico',
  NY: 'New York',
  NC: 'North Carolina',
  ND: 'North Dakota',
  OH: 'Ohio',
  OK: 'Oklahoma',
  OR: 'Oregon',
  PA: 'Pennsylvania',
  RI: 'Rhode Island',
  SC: 'South Carolina',
  SD: 'South Dakota',
  TN: 'Tennessee',
  TX: 'Texas',
  UT: 'Utah',
  VT: 'Vermont',
  VA: 'Virginia',
  WA: 'Washington',
  WV: 'West Virginia',
  WI: 'Wisconsin',
  WY: 'Wyoming',
  DC: 'District of Columbia',
};

type StateLegalTextConfig = {
  firstText: string;
  secondText: string | ((enterpriseName: string) => string);
};

const stateLegalText: Partial<Record<string, StateLegalTextConfig>> = {
  OH: {
    firstText:
      'Rewards and agree to receive marketing emails and SMS (msg & data rates may apply). Unsubscribe anytime by email link, preference center, or replying STOP to SMS. By continuing, you agree to our ',
    secondText: (enterpriseName: string) =>
      `. I understand that the data collected by ${enterpriseName} for the purposes of administering this loyalty program will not be transmitted to or stored by any state governments`,
  },
};
const defaultText =
  'Rewards and agree to receive marketing emails and SMS (msg & data rates may apply). Unsubscribe anytime by email link, preference center, or replying STOP to SMS. By continuing, you agree to our ';

export function LandingPage({
  enterprise,
  onStartEarningRewards,
  onLikelyStateDetected,
  themeDispensary,
  brandingStatus = 'fallback',
  visitorGeo,
}: LandingPageProps): JSX.Element {
  // Only a decision finalized as "custom" may apply real values. Once the parent freezes
  // "fallback" after its deadline, a late theme response cannot restyle visible controls.
  const useCustomBranding = brandingStatus === 'custom';
  const customization = useMemo(
    () => (useCustomBranding ? getWebCustomizationSettings(themeDispensary ?? undefined) : null),
    [themeDispensary, useCustomBranding]
  );
  const realButtonsLinksColor = customization?.colors.buttonsLinks ?? null;
  const realControlRadius = customization?.radius?.buttons ?? null;
  const realButtonTextTransform = customization?.textTransforms?.buttons ?? null;
  const showBrandingSkeleton = brandingStatus === 'loading';

  const controlStyleVars = useMemo(
    (): React.CSSProperties =>
      ({
        ...(realButtonsLinksColor
          ? {
              '--dcm-enterprise-signup-cta-bg': realButtonsLinksColor,
              '--dcm-enterprise-signup-cta-bg-hover': TinyColor(realButtonsLinksColor).darken(8).toString(),
              '--dcm-enterprise-signup-link-color': realButtonsLinksColor,
            }
          : {}),
        ...(realControlRadius ? { '--dcm-enterprise-signup-control-radius': realControlRadius } : {}),
        ...(realButtonTextTransform ? { '--dcm-enterprise-signup-cta-text-transform': realButtonTextTransform } : {}),
      } as React.CSSProperties),
    [realButtonsLinksColor, realControlRadius, realButtonTextTransform]
  );
  const termsUrl = enterprise?.termsAndConditionsUrl ?? DUTCHIE_TERMS_URL;
  const privacyUrl = enterprise?.privacyPolicyUrl ?? DUTCHIE_PRIVACY_URL;
  const stateOptions = useMemo(
    () =>
      (enterprise?.dposLoyaltyIsoRegionCodes ?? [])
        .filter((code): code is string => code != null)
        .map((code) => {
          const stateCode = code.replace('US-', '');
          return {
            code: stateCode,
            name: US_STATES[stateCode] || stateCode,
          };
        })
        .sort((a, b) => a.name.localeCompare(b.name)),
    [enterprise?.dposLoyaltyIsoRegionCodes]
  );

  // Auto-select if only one state available
  const initialState = stateOptions.length === 1 ? stateOptions[0].code : '';
  const [selectedState, setSelectedState] = useState<string>(initialState);
  const hasUserSelectedStateRef = useRef(false);

  // Single-region enterprises are known synchronously -- let the parent start priming colors
  // immediately instead of waiting on the geolocation effect below.
  useEffect(() => {
    if (initialState) {
      onLikelyStateDetected?.(initialState);
    }
  }, [initialState, onLikelyStateDetected]);

  // Reverse-geocodes coordinates and, on a match against this enterprise's configured regions,
  // pre-fills the dropdown and notifies the parent for theme priming. Shared by both the
  // server-provided (visitorGeo) and client-geolocation detection paths below.
  const applyDetectedCoordinates = useCallback(
    (latitude: number, longitude: number, isCancelledRef: { current: boolean }): void => {
      void getLocationsFromCoordinates({ latitude, longitude }).then(({ data, status }) => {
        if (isCancelledRef.current || status !== 200 || hasUserSelectedStateRef.current) {
          return;
        }
        const detectedState = data[0]?.state;
        const matchedOption = stateOptions.find((option) => option.code === detectedState);
        if (matchedOption) {
          setSelectedState(matchedOption.code);
          onLikelyStateDetected?.(matchedOption.code);
        }
      });
    },
    [onLikelyStateDetected, stateOptions]
  );

  // POS-17999 -- when the server already resolved the visitor's approximate location (Cloudflare
  // edge IP-geo headers, see page.tsx), use it immediately instead of waiting on the client
  // navigator.geolocation permission prompt below -- no dialog, no denial, available on mount.
  useEffect(() => {
    if (stateOptions.length <= 1 || !visitorGeo) {
      return undefined;
    }
    const isCancelledRef = { current: false };
    applyDetectedCoordinates(visitorGeo.lat, visitorGeo.lng, isCancelledRef);
    return () => {
      isCancelledRef.current = true;
    };
  }, [applyDetectedCoordinates, stateOptions.length, visitorGeo]);

  // Silently auto-detect the visitor's state so the dropdown is pre-filled with their likely
  // location instead of forcing a manual search -- this is background detection, not a
  // user-initiated action, so failures/denials fall back quietly to the manual dropdown. Skipped
  // entirely when visitorGeo already resolved this server-side (above) -- no need for the
  // permission-prompt path too.
  useEffect(() => {
    // useEffect only runs client-side, so `navigator` is always defined here.
    if (stateOptions.length <= 1 || !navigator.geolocation || visitorGeo) {
      return undefined;
    }

    const cancelledRef = { current: false };
    navigator.geolocation.getCurrentPosition((position) => {
      const { latitude, longitude } = position.coords;
      applyDetectedCoordinates(latitude, longitude, cancelledRef);
    });
    return () => {
      cancelledRef.current = true;
    };
  }, [applyDetectedCoordinates, stateOptions.length, visitorGeo]);

  const handleStartClick = (): void => {
    if (!selectedState) {
      return;
    }
    onStartEarningRewards(selectedState);
  };

  const buildLegalText = (): string => {
    const legalConfig = stateLegalText[selectedState];
    if (!legalConfig) {
      return '';
    }

    const enterpriseName = String(enterprise?.displayName ?? enterprise?.name ?? 'Company');
    const secondText =
      typeof legalConfig.secondText === 'function' ? legalConfig.secondText(enterpriseName) : legalConfig.secondText;

    return secondText;
  };

  return (
    <>
      <MetaTags title='Join Our Rewards Program' description='Earn exclusive benefits with every purchase' />

      <LandingContainer>
        <LandingCard style={controlStyleVars} data-testid='enterprise-signup-landing-card'>
          <IconContainer>
            <RibbonStarV2 />
          </IconContainer>

          <LandingHeader>
            <LandingTitle>Join {String(enterprise?.displayName ?? enterprise?.name ?? 'Company/Program')}</LandingTitle>
            <LandingSubtitle>
              {String(
                enterprise?.description ??
                  'Earn rewards by creating an account to unlock exclusive benefits with every purchase'
              )}
            </LandingSubtitle>
          </LandingHeader>

          {stateOptions.length > 0 && (
            <StateSelectionSection>
              <StateLabel htmlFor='state-select'>Please select your state:</StateLabel>
              {showBrandingSkeleton ? (
                <ControlSkeleton aria-hidden='true' data-testid='state-select-skeleton' />
              ) : (
                <StateSelect
                  id='state-select'
                  value={selectedState}
                  onChange={(e) => {
                    hasUserSelectedStateRef.current = true;
                    setSelectedState(e.target.value);
                    onLikelyStateDetected?.(e.target.value);
                  }}
                  onBlur={(e) => {
                    hasUserSelectedStateRef.current = true;
                    setSelectedState(e.target.value);
                  }}
                  aria-label='Select your state'
                >
                  <option value='' disabled>
                    State
                  </option>
                  {stateOptions.map(({ code, name }) => (
                    <option key={code} value={code}>
                      {name}
                    </option>
                  ))}
                </StateSelect>
              )}
            </StateSelectionSection>
          )}

          {showBrandingSkeleton ? (
            <ControlSkeleton $marginBottom='2rem' aria-hidden='true' data-testid='start-button-skeleton' />
          ) : (
            <StartButton onClick={handleStartClick} disabled={stateOptions.length > 0 && !selectedState}>
              Start earning rewards
            </StartButton>
          )}

          <LegalText>
            By creating an account, you'll be enrolled in{' '}
            {String(enterprise?.displayName ?? enterprise?.name ?? 'Company')}{' '}
            {stateLegalText[selectedState]?.firstText ?? defaultText}
            <LegalLink
              $brandingLoading={showBrandingSkeleton}
              href={termsUrl}
              target='_blank'
              rel='noopener noreferrer'
            >
              Terms
            </LegalLink>{' '}
            and{' '}
            <LegalLink
              $brandingLoading={showBrandingSkeleton}
              href={privacyUrl}
              target='_blank'
              rel='noopener noreferrer'
            >
              Privacy Policy
            </LegalLink>
            {buildLegalText()}.
          </LegalText>
        </LandingCard>
      </LandingContainer>
    </>
  );
}

// Styled Components for Landing Page
const LandingContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
  padding: 1rem;
`;

const LandingCard = styled.div`
  background: white;
  border-radius: 16px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  padding: 3rem 2rem;
  width: 100%;
  max-width: 500px;
  text-align: center;
`;

const IconContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 2rem;

  svg {
    width: 80px;
    height: 80px;
  }
`;

const LandingHeader = styled.div`
  margin-bottom: 2rem;
`;

const LandingTitle = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 1rem 0;
  line-height: 1.2;
`;

const LandingSubtitle = styled.p`
  font-size: 1.125rem;
  color: #4b5563;
  margin: 0;
  line-height: 1.5;
`;

const StateSelectionSection = styled.div`
  margin-bottom: 2rem;
  text-align: left;
`;

const StateLabel = styled.label`
  display: block;
  font-size: 1.125rem;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 1rem;
`;

const StateSelect = styled.select`
  width: 100%;
  padding: 1rem;
  font-size: 1rem;
  color: #1f2937;
  background-color: white;
  border: 2px solid #e5e7eb;
  border-radius: var(--dcm-enterprise-signup-control-radius, 12px);
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath fill='%236b7280' d='M1.41 0L6 4.59 10.59 0 12 1.41l-6 6-6-6z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 1rem center;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: #3b82f6;
  }

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  option {
    padding: 0.5rem;
  }

  option:disabled {
    color: #9ca3af;
  }
`;

type StartButtonProps = { disabled?: boolean };

// POS-17999 -- real color (solid fill, no synthesized gradient) via --dcm-enterprise-signup-
// cta-bg/-hover, set inline where this renders below. Threaded through a CSS var (not baked in
// directly) so an Ecommerce Pro SDK theme can still override it. Falls back to the original
// gradient when no real color is known.
const StartButton = styled(Button)<StartButtonProps>`
  width: 100%;
  background: ${({ disabled }) =>
    disabled ? '#e5e7eb' : 'var(--dcm-enterprise-signup-cta-bg, linear-gradient(135deg, #60a5fa 0%, #3b82f6 100%))'};
  color: ${({ disabled }) => (disabled ? '#9ca3af' : 'white')};
  font-size: 1rem;
  font-weight: 600;
  padding: 1rem 2rem;
  border-radius: var(--dcm-enterprise-signup-control-radius, 12px);
  border: none;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  margin-bottom: 2rem;
  text-transform: var(--dcm-enterprise-signup-cta-text-transform, uppercase);
  letter-spacing: 0.5px;
  transition: all 0.2s ease;

  &:hover {
    background: ${({ disabled }) =>
      disabled
        ? '#e5e7eb'
        : 'var(--dcm-enterprise-signup-cta-bg-hover, linear-gradient(135deg, #3b82f6 0%, #2563eb 100%))'};
    transform: ${({ disabled }) => (disabled ? 'none' : 'translateY(-2px)')};
    box-shadow: ${({ disabled }) => (disabled ? 'none' : '0 8px 20px rgba(59, 130, 246, 0.3)')};
  }

  &:active {
    transform: ${({ disabled }) => (disabled ? 'none' : 'translateY(0)')};
  }
`;

// POS-17999 -- shown instead of StartButton/StateSelect while their real branding is still
// resolving, so nothing visibly changes color/shape after first paint (the flicker this replaces).
const skeletonShimmer = keyframes`
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
`;

const ControlSkeleton = styled.div<{ $marginBottom?: string }>`
  width: 100%;
  height: 58px;
  border-radius: var(--dcm-enterprise-signup-control-radius, 12px);
  margin-bottom: ${({ $marginBottom }) => $marginBottom ?? '0'};
  background: linear-gradient(90deg, #e5e7eb 25%, #f3f4f6 50%, #e5e7eb 75%);
  background-size: 200% 100%;
  animation: ${skeletonShimmer} 1.5s ease-in-out infinite;
`;

const LegalText = styled.p`
  font-size: 0.75rem;
  color: #6b7280;
  line-height: 1.4;
  margin: 0;
  text-align: left;
`;

const LegalLink = styled.a<{ $brandingLoading: boolean }>`
  color: ${({ $brandingLoading }) =>
    $brandingLoading ? 'inherit' : 'var(--dcm-enterprise-signup-link-color, #3b82f6)'};

  &:active,
  &:focus,
  &:hover {
    text-decoration: underline;
  }
`;
